import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Dropdown, DropdownOption } from '@/form/FormComponents';
import { internalAction } from '@/data/loaders';
import { Spinner } from '@/components/Spinner';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// black: #212121
// account_created

// gray: #757575
// logged_in

// dark blue: #0d47a1
// created_scenario

// light blue: #64b5f6
// duplicated_scenario
// updated_scenario

// dark green: #33691e
// ran_scenario
// ran_scenario_and_interacted

// light green: #aed581
// ran_scenario_with_voice
// concluded_scenario

// green: #7cb342
// completed_scenario

// dark red: #b71c1c
// created_rubric

// red: #e53935
// uploaded_rubric

// light red: #e57373
// updated_rubric
// duplicated_rubric

// dark purple: #4a148c
// created_course
// joined_course_as_student
// joined_course_as_educator

// purple: #8e24aa
// created_assessment

// light purple: #ba68c8
// updated_assessment
// updated_assessment_scenario

// dark orange: #e65100
// student_invited_or_joined_course
// educator_invited_or_joined_course

// orange: #fb8c00
// student_completed_assessment

// light orange: #ffb74d
// returned_assessment_submission
// updated_assessment_submission_feedback

// Activity type configuration with colors, labels, and height multipliers
const ACTIVITY_CONFIG = {
  account_created: {
    color: '#000000',
    label: 'Account created',
    height: 5
  },
  logged_in: {
    color: '#757575',
    label: 'Logged in',
    height: 1
  },
  created_course: {
    color: '#6a1b9a',
    label: 'Created course',
    height: 5
  },
  student_invited_or_joined_course: {
    color: '#ff6f00',
    label: 'Invited student to course',
    height: 1
  },
  educator_invited_or_joined_course: {
    color: '#ac1900',
    label: 'Invited co-educator to course',
    height: 1
  },
  joined_course_as_student: {
    color: '#6a1b9a',
    label: 'Joined course as student',
    height: 1
  },
  joined_course_as_educator: {
    color: '#6a1b9a',
    label: 'Joined course as co-educator',
    height: 1
  },
  created_scenario: {
    color: '#01579b',
    label: 'Created scenario',
    height: 3
  },
  duplicated_scenario: {
    color: '#64b5f6',
    label: 'Duplicated scenario',
    height: 1
  },
  updated_scenario: {
    color: '#64b5f6',
    label: 'Edited scenario',
    height: 1
  },
  created_rubric: {
    color: '#c62828',
    label: 'Created rubric',
    height: 3
  },
  uploaded_rubric: {
    color: '#e53935',
    label: 'Uploaded rubric PDF',
    height: 2
  },
  updated_rubric: {
    color: '#e57373',
    label: 'Edited rubric',
    height: 1
  },
  duplicated_rubric: {
    color: '#e57373',
    label: 'Duplicated rubric',
    height: 1
  },
  ran_scenario: {
    color: '#aed581',
    label: 'Ran scenario, didn\'t interact',
    height: 1
  },
  ran_scenario_and_interacted: {
    color: '#7cb342',
    label: 'Ran scenario, interacted, didn\'t conclude',
    height: 1
  },
  concluded_scenario: {
    color: '#7cb342',
    label: 'Concluded scenario, didn\'t submit',
    height: 1
  },
  completed_scenario: {
    color: '#33691e',
    label: 'Completed scenario and got feedback',
    height: 1
  },
  ran_scenario_with_voice: {
    color: '#26a69a',
    label: 'Used voice',
    height: 0.5
  },
  created_assessment: {
    color: '#8e24aa',
    label: 'Created assessment',
    height: 3
  },
  updated_assessment: {
    color: '#ba68c8',
    label: 'Edited assessment',
    height: 1
  },
  updated_assessment_scenario: {
    color: '#ba68c8',
    label: 'Edited assessment scenario',
    height: 1
  },
  student_completed_assessment: {
    color: '#fb8c00',
    label: 'Their student submitted an assessment',
    height: 1
  },
  returned_assessment_submission: {
    color: '#ffb74d',
    label: 'Returned an assessment to a student',
    height: 1
  },
  updated_assessment_submission_feedback: {
    color: '#ffb74d',
    label: 'Edited feedback on an assessment submission',
    height: 1
  }
};

export function ActivityGraph({ contact_id, account_id, uid, mainPage = false }) {
  const [resolution, setResolution] = useState(mainPage ? '30_days' : '14_days');
  const [grouping, setGrouping] = useState('activity'); // 'activity' or 'user'

  // Parse resolution for the query
  const [count, unit] = resolution.split('_');
  const requestData = {};
  
  // Only include contact_id, account_id, or uid if provided
  if (uid) {
    requestData.uid = uid;
  } else if (contact_id) {
    requestData.contact_id = contact_id;
  }
  if (account_id) {
    requestData.account_id = account_id;
  }

  if (unit === 'days') {
    requestData.days = parseInt(count);
  } else if (unit === 'weeks') {
    requestData.weeks = parseInt(count);
  } else if (unit === 'months') {
    requestData.months = parseInt(count);
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ['activityGraph', contact_id, account_id, uid, resolution],
    queryFn: () => internalAction('getActivityGraph', requestData).then((response) => response.data)
  });

  // Render content based on state
  let chartContent;

  if (isLoading) {
    chartContent = (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Spinner style={{ width: '40px', height: '40px' }} />
      </div>
    );
  } else if (error) {
    chartContent = (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--error)' }}>
        Failed to load activity data
      </div>
    );
  } else if (data?.has_signed_up === false) {
    chartContent = (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--error)' }}>
        {account_id ? "Nobody has signed in yet" : "Has not signed in yet"}
      </div>
    );
  } else if (data && data.labels) {
    // Prepare chart data based on grouping
    let chartDatasets = [];
    
    if (grouping === 'activity' || !data.byUser || Object.keys(data.byUser).length === 0) {
      // Group by activity type (or if no user data available)
      if (data.datasets && data.datasets.length > 0) {
        // Create a map of datasets by their label for easy lookup
        const datasetMap = {};
        data.datasets.forEach(dataset => {
          datasetMap[dataset.label] = dataset;
        });
        
        // Build datasets in the order defined by ACTIVITY_CONFIG
        chartDatasets = Object.keys(ACTIVITY_CONFIG)
          .filter(activityType => datasetMap[activityType]) // Only include activities that have data
          .map(activityType => {
            const dataset = datasetMap[activityType];
            const config = ACTIVITY_CONFIG[activityType];
            const heightMultiplier = config?.height || 1;
            
            return {
              label: config?.label || activityType,
              data: dataset.data.map(value => value * heightMultiplier),
              backgroundColor: config?.color || '#94A3B8',
              borderWidth: 0,
              heightMultiplier: heightMultiplier // Store for tooltip
            };
          });
      }
    } else {
      // Group by user
      const userColors = [
        '#1e88e5', '#e53935', '#5e35b1', '#43a047', '#ffb300', '#f4511e', '#00897b', '#6d4c41', '#d81b60', '#546e7a', '#3949ab', '#9e9d24'
      ];
      
      let colorIndex = 0;
      Object.entries(data.byUser).forEach(([uid, activityData]) => {
        // Sum all activities for this user at each time point
        const userData = data.labels.map((_, index) => {
          let total = 0;
          Object.values(activityData).forEach(activityArray => {
            total += activityArray[index] || 0;
          });
          return total;
        });

        chartDatasets.push({
          label: data.users[uid] || 'Deleted User',
          data: userData,
          backgroundColor: userColors[colorIndex % userColors.length],
          borderWidth: 0
        });
        colorIndex++;
      });
    }

    const chartData = {
      labels: data.labels,
      datasets: chartDatasets
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false // Don't show legend as requested
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          filter: function(tooltipItem) {
            // Only show items with non-zero values
            const heightMultiplier = tooltipItem.dataset.heightMultiplier || 1;
            const originalValue = Math.round(tooltipItem.parsed.y / heightMultiplier);
            return originalValue > 0;
          },
          itemSort: function(a, b) {
            // Reverse the order to match visual stacking (top to bottom)
            return b.datasetIndex - a.datasetIndex;
          },
          callbacks: {
            title: function(context) {
              if (!context || context.length === 0) return '';
              return context[0].label;
            },
            label: function(context) {
              // Divide back by height multiplier to show original count
              const heightMultiplier = context.dataset.heightMultiplier || 1;
              const originalValue = Math.round(context.parsed.y / heightMultiplier);
              return `${context.dataset.label}: ${originalValue}`;
            }
          }
        }
      },
      scales: {
        x: {
          stacked: true,
          grid: {
            display: false
          },
          ticks: {
            maxRotation: 45,
            minRotation: 0,
            font: {
              size: 10
            }
          }
        },
        y: {
          stacked: true,
          beginAtZero: true,
          suggestedMax: 6,
          ticks: {
            display: false,
            precision: 0,
            font: {
              size: 10
            }
          },
          grid: {
            color: 'rgba(0, 0, 0, 0.05)'
          }
        }
      },
      interaction: {
        mode: 'point',
        intersect: true
      }
    };

    chartContent = <Bar data={chartData} options={options} />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ marginBottom: '12px', display: 'flex', gap: '8px', flexShrink: 0, justifyContent: 'space-between' }}>
        {mainPage ? (
          <Dropdown 
            value={resolution} 
            onChange={setResolution}
          >
            <DropdownOption name="14_days" text="Last 14 Days" />
            <DropdownOption name="30_days" text="Last 30 Days" />
            <DropdownOption name="60_days" text="Last 60 Days" />
            <DropdownOption name="90_days" text="Last 90 Days" />
            <DropdownOption name="26_weeks" text="Last 26 Weeks" />
            <DropdownOption name="52_weeks" text="Last 52 Weeks" />
            <DropdownOption name="12_months" text="Last 12 Months" />
            <DropdownOption name="24_months" text="Last 24 Months" />
          </Dropdown>
        ) : (
          <Dropdown 
            value={resolution} 
            onChange={setResolution}
          >
            <DropdownOption name="7_days" text="Last 7 Days" />
            <DropdownOption name="14_days" text="Last 14 Days" />
            <DropdownOption name="30_days" text="Last 30 Days" />
            <DropdownOption name="12_weeks" text="Last 12 Weeks" />
            <DropdownOption name="26_weeks" text="Last 26 Weeks" />
            <DropdownOption name="12_months" text="Last 12 Months" />
          </Dropdown>
        )}
        {!contact_id && !uid && data && data.byUser && (
          <Dropdown 
            value={grouping} 
            onChange={setGrouping}
          >
            <DropdownOption name="activity" text="By Activity Type" />
            <DropdownOption name="user" text="By User" />
          </Dropdown>
        )}
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>
        {chartContent}
      </div>
    </div>
  );
}
